import { OngewensteGebeurtenis, useGetRelevanteOngewensteGebeurtenissen } from "@/entities/ongewenste-gebeurtenis";
import { ErrorDisplay } from "@/shared/components/error";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { OngewensteGebeurtenisCard } from "./ongewenste-gebeurtenis-card.tsx";
import { SkeletonDossierCard } from "./ui";

type OngewensteGebeurtenisListProps = {
    onCreateFromSpoorwebDossier: (ogNummer: string) => void;
};

export function OngewensteGebeurtenisList({ onCreateFromSpoorwebDossier }: Readonly<OngewensteGebeurtenisListProps>) {
    const prefersReducedMotion = useReducedMotion();
    const { data: relevanteOngewensteGebeurtenissen, isLoading, error } = useGetRelevanteOngewensteGebeurtenissen();

    return (
        <ol
            className="gap-s flex max-w-[656px] shrink-0 flex-row overflow-x-auto overflow-y-hidden py-3 pl-4"
            data-testid="ongewenste-gebeurtenissen-list"
        >
            {isLoading && <SkeletonDossierCard testId="ongewenste-gebeurtenis-skeleton" />}
            {error && (
                <ErrorDisplay
                    error={error}
                    onRetry={() => window.location.reload()}
                    title="Fout bij het laden van ongewenste gebeurtenissen"
                />
            )}
            {prefersReducedMotion ? (
                <NotAnimatedList
                    onCreateFromSpoorwebDossier={onCreateFromSpoorwebDossier}
                    relevanteOgs={relevanteOngewensteGebeurtenissen}
                />
            ) : (
                <AnimatedList onCreateFromSpoorwebDossier={onCreateFromSpoorwebDossier} relevanteOgs={relevanteOngewensteGebeurtenissen} />
            )}
        </ol>
    );
}

type OgListProps = OngewensteGebeurtenisListProps & {
    relevanteOgs: OngewensteGebeurtenis[] | undefined;
};

function AnimatedList({ relevanteOgs, onCreateFromSpoorwebDossier }: OgListProps) {
    return (
        <AnimatePresence>
            {relevanteOgs?.map((ongewensteGebeurtenis) => (
                <motion.li layout key={ongewensteGebeurtenis.nummer} data-name="motion.li">
                    <OngewensteGebeurtenisCard
                        animated={ongewensteGebeurtenis.isRealtimeUpdate}
                        status={ongewensteGebeurtenis.status}
                        volgnummer={ongewensteGebeurtenis.volgnummer}
                        spoorwebNummer={ongewensteGebeurtenis.nummer}
                        omschrijving={ongewensteGebeurtenis.omschrijving}
                        trajectOfBaanvak={ongewensteGebeurtenis.baanvak}
                        onClick={() => onCreateFromSpoorwebDossier(ongewensteGebeurtenis.nummer)}
                    />
                </motion.li>
            ))}
        </AnimatePresence>
    );
}

function NotAnimatedList({ relevanteOgs, onCreateFromSpoorwebDossier }: OgListProps) {
    return (
        <>
            {relevanteOgs?.map((ongewensteGebeurtenis) => (
                <OngewensteGebeurtenisCard
                    key={ongewensteGebeurtenis.nummer}
                    animated={false}
                    status={ongewensteGebeurtenis.status}
                    volgnummer={ongewensteGebeurtenis.volgnummer}
                    spoorwebNummer={ongewensteGebeurtenis.nummer}
                    omschrijving={ongewensteGebeurtenis.omschrijving}
                    trajectOfBaanvak={ongewensteGebeurtenis.baanvak}
                    onClick={() => onCreateFromSpoorwebDossier(ongewensteGebeurtenis.nummer)}
                />
            ))}
        </>
    );
}
